import styled from 'styled-components';
import { theme } from '../theme';

// Profile Editor Container
export const EditorContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  background: ${theme.colors.background.card};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.light};
  overflow: hidden;
  box-shadow: ${theme.shadows.medium};
`;

export const EditorHeader = styled.div`
  padding: 2rem;
  border-bottom: 1px solid ${theme.colors.border.light};
  background: ${theme.colors.background.secondary};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 1.25rem 1rem;
  }
`;

export const EditorTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.5rem;
    margin-bottom: 0.375rem;
  }
`;

export const EditorNavigation = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Ensure tabs don't shrink on mobile */
  flex-wrap: nowrap;
  min-width: 100%;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: 0.25rem;
    margin-top: 0.75rem;
    padding-bottom: 0.25rem; /* Space for scroll indicator */
  }
  
  @media (max-width: 480px) {
    gap: 0.125rem;
  }
`;

export const EditorTab = styled.button<{ $isActive: boolean }>`
  background: ${props => props.$isActive 
    ? theme.colors.gradients.primary
    : 'transparent'};
  border: 1px solid ${props => props.$isActive 
    ? 'transparent' 
    : theme.colors.border.medium};
  color: ${props => props.$isActive ? 'white' : theme.colors.text.primary};
  padding: 0.75rem 1rem;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
  white-space: nowrap; /* Prevent text wrapping */
  flex-shrink: 0; /* Prevent tabs from shrinking */
  min-width: fit-content;

  &:hover {
    background: ${props => props.$isActive 
      ? theme.colors.gradients.primary
      : theme.colors.background.tertiary};
    border-color: ${props => props.$isActive ? 'transparent' : theme.colors.primary};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0.5rem 0.625rem;
    font-size: 0.75rem;
    border-radius: ${theme.borderRadius.sm};
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0.5rem;
    font-size: 0.7rem;
    min-width: 44px; /* Touch target size */
  }
`;

export const EditorContent = styled.div`
  padding: 2rem;
  min-height: 500px;
  background: ${theme.colors.background.card};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 1.25rem 1rem;
    min-height: 400px;
  }
`;

export const EditorActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-top: 1px solid ${theme.colors.border.light};
  background: ${theme.colors.background.secondary};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem;
    
    > div {
      width: 100%;
      display: flex;
      gap: 0.5rem;
      
      &:first-child {
        order: 2; /* Cancel/Preview buttons below Save */
      }
      
      &:last-child {
        order: 1; /* Save button on top */
      }
    }
  }
`;

export const SaveButton = styled.button`
  background: ${theme.colors.gradients.primary};
  border: none;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.heavy};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 100%;
    padding: 0.875rem 1.25rem;
    font-size: 0.95rem;
    min-height: 44px; /* Touch target size */
  }
`;

export const CancelButton = styled.button`
  background: transparent;
  border: 1px solid ${theme.colors.border.medium};
  color: ${theme.colors.text.primary};
  padding: 0.75rem 1.5rem;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    background: ${theme.colors.background.tertiary};
    border-color: ${theme.colors.border.dark};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex: 1;
    padding: 0.875rem 1rem;
    font-size: 0.9rem;
    min-height: 44px; /* Touch target size */
  }
`;

export const PreviewButton = styled.button`
  background: ${theme.colors.secondary};
  border: none;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.medium};
    background: ${theme.colors.hover.secondary};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex: 1;
    padding: 0.875rem 1rem;
    font-size: 0.9rem;
    min-height: 44px; /* Touch target size */
  }
`;

export const EditorStatus = styled.div`
  padding: 1rem 2rem;
  background: ${theme.colors.background.secondary};
  border-top: 1px solid ${theme.colors.border.light};
`;

export const StatusMessage = styled.div<{ $type: 'success' | 'error' | 'warning' }>`
  color: ${props => {
    switch (props.$type) {
      case 'success': return theme.colors.success;
      case 'error': return theme.colors.error;
      case 'warning': return theme.colors.warning;
      default: return theme.colors.text.primary;
    }
  }};
  font-size: 0.9rem;
  font-weight: 500;
`;

// Photo Manager Styles
export const PhotoManagerContainer = styled.div`
  padding: 1rem;
`;

export const PhotoManagerHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

export const PhotoManagerTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

export const PhotoManagerDescription = styled.div`
  font-size: 1rem;
  color: ${theme.colors.text.secondary};
  line-height: 1.5;
`;

export const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }
`;

export const PhotoSlot = styled.div<{ $isMain?: boolean; $isEmpty?: boolean }>`
  position: relative;
  aspect-ratio: 3/4;
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  border: 2px solid ${props => props.$isMain ? theme.colors.primary : theme.colors.border.medium};
  background: ${props => props.$isEmpty ? theme.colors.background.secondary : 'transparent'};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.medium};
    border-color: ${props => props.$isMain ? theme.colors.primary : theme.colors.primary};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    border-width: 1.5px;
    border-radius: ${theme.borderRadius.md};
  }
`;

export const PhotoPreview = styled.div`
  position: relative;
  width: 100%;
  height: 100%;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .main-badge {
    position: absolute;
    top: 0.5rem;
    left: 0.5rem;
    background: ${theme.colors.primary};
    color: white;
    padding: 0.25rem 0.5rem;
    border-radius: ${theme.borderRadius.md};
    font-size: 0.7rem;
    font-weight: 600;

    @media (max-width: ${theme.breakpoints.mobile}) {
      top: 0.25rem;
      left: 0.25rem;
      padding: 0.2rem 0.4rem;
      font-size: 0.6rem;
      border-radius: ${theme.borderRadius.sm};
    }
  }
`;

export const PhotoActions = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0.5rem;
    gap: 0.25rem;
    flex-wrap: wrap;
  }

  button {
    @media (max-width: ${theme.breakpoints.mobile}) {
      font-size: 0.65rem;
      padding: 0.2rem 0.4rem;
      min-height: 28px;
    }
  }
`;

export const PhotoOrder = styled.span`
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0.2rem 0.4rem;
    font-size: 0.65rem;
    border-radius: 8px;
  }
`;

export const PhotoValidation = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    top: 0.25rem;
    right: 0.25rem;
  }
`;

export const ValidationStatus = styled.div<{ $status: 'pending' | 'approved' | 'rejected' }>`
  background: ${props => {
    switch (props.$status) {
      case 'pending': return theme.colors.warning;
      case 'approved': return theme.colors.success;
      case 'rejected': return theme.colors.error;
      default: return theme.colors.border.medium;
    }
  }};
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: ${theme.borderRadius.md};
  font-size: 0.7rem;
  font-weight: 600;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0.2rem 0.4rem;
    font-size: 0.6rem;
    border-radius: ${theme.borderRadius.sm};
    line-height: 1.2;
  }
`;

export const UploadArea = styled.div<{ $isDragOver: boolean }>`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.$isDragOver 
    ? `${theme.colors.primary}20` 
    : theme.colors.background.secondary};
  border: 2px dashed ${props => props.$isDragOver 
    ? theme.colors.primary 
    : theme.colors.border.medium};

  &:hover {
    background: ${theme.colors.background.tertiary};
    border-color: ${theme.colors.primary};
  }

  .upload-icon {
    font-size: 2rem;
    margin-bottom: 0.5rem;
    color: ${theme.colors.text.secondary};
  }

  .upload-text {
    font-size: 0.9rem;
    color: ${theme.colors.text.secondary};
    text-align: center;
  }
`;

export const UploadButton = styled.button`
  background: ${theme.colors.gradients.primary};
  border: none;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.3s ease;
  margin: 0 auto;
  display: block;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.heavy};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

export const RemoveButton = styled.button`
  background: #ff4757;
  border: none;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.7rem;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background: #ff3742;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0.2rem 0.4rem;
    font-size: 0.65rem;
    border-radius: 3px;
    min-height: 28px;
  }
`;

export const SetMainButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.8);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.7rem;
  font-weight: 600;
  transition: all 0.3s ease;
  backdrop-filter: blur(4px);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: white;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0.2rem 0.4rem;
    font-size: 0.65rem;
    border-radius: 3px;
    min-height: 28px;
  }
`;

export const ReorderButton = styled.button`
  background: ${theme.colors.warning};
  border: none;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  font-size: 0.7rem;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.9;
  }
`;

export const DragHandle = styled.div`
  cursor: grab;
  color: ${theme.colors.text.secondary};
  font-size: 1.2rem;

  &:active {
    cursor: grabbing;
  }
`;

export const PhotoTips = styled.div`
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.md};
  padding: 1rem;
  margin-top: 1rem;
  border: 1px solid ${theme.colors.border.light};

  h4 {
    color: ${theme.colors.text.primary};
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
`;

export const TipItem = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
`;

export const ErrorMessage = styled.div`
  color: #ff4757;
  font-size: 0.9rem;
  margin-top: 1rem;
  padding: 0.75rem;
  background: rgba(255, 71, 87, 0.1);
  border: 1px solid rgba(255, 71, 87, 0.3);
  border-radius: 8px;
`;

export const SuccessMessage = styled.div`
  color: #2ed573;
  font-size: 0.9rem;
  margin-top: 1rem;
  padding: 0.75rem;
  background: rgba(46, 213, 115, 0.1);
  border: 1px solid rgba(46, 213, 115, 0.3);
  border-radius: 8px;
`;

export const PhotoStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

export const StatItem = styled.div`
  text-align: center;
  padding: 1rem;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.light};

  .stat-value {
    display: block;
    font-size: 1.5rem;
    font-weight: 700;
    color: ${theme.colors.primary};
    margin-bottom: 0.25rem;
  }

  .stat-label {
    font-size: 0.8rem;
    color: ${theme.colors.text.secondary};
  }
`;

// Bio Editor Styles
export const BioEditorContainer = styled.div`
  padding: 1rem;
`;

export const BioEditorHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

export const BioEditorTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

export const BioEditorDescription = styled.p`
  font-size: 1rem;
  color: ${theme.colors.text.secondary};
  line-height: 1.5;
`;

export const BioStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

export const BioEditorContent = styled.div`
  margin-bottom: 2rem;
`;

export const BioActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
`;

export const FormatButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export const FormatButton = styled.button`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.medium};
  color: ${theme.colors.text.primary};
  padding: 0.5rem;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.3s ease;

  &:hover {
    background: ${theme.colors.background.tertiary};
    border-color: ${theme.colors.primary};
  }
`;

export const ActionButton = styled.button`
  background: ${theme.colors.gradients.primary};
  border: none;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.heavy};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

export const BioTextarea = styled.textarea`
  width: 100%;
  min-height: 200px;
  padding: 1rem;
  background: ${theme.colors.background.card};
  border: 1px solid ${theme.colors.border.medium};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.primary};
  font-size: 1rem;
  line-height: 1.5;
  resize: vertical;
  font-family: inherit;

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px ${theme.colors.primary}20;
  }
`;

export const BioCounter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
`;

export const CharacterCount = styled.span`
  font-size: 0.8rem;
  color: ${theme.colors.text.secondary};
`;

export const WordCount = styled.span`
  font-size: 0.8rem;
  color: ${theme.colors.text.secondary};
`;

export const BioSuggestions = styled.div`
  margin-top: 1rem;
`;

export const SuggestionItem = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.md};
  padding: 1rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${theme.colors.background.tertiary};
    border-color: ${theme.colors.primary};
  }

  h4 {
    color: ${theme.colors.text.primary};
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  p {
    color: ${theme.colors.text.secondary};
    font-size: 0.8rem;
    line-height: 1.4;
  }
`;

export const ValidationMessage = styled.div<{ $type: 'error' | 'warning' | 'success' }>`
  color: ${props => {
    switch (props.$type) {
      case 'error': return '#ff4757';
      case 'warning': return '#ffa502';
      case 'success': return '#2ed573';
      default: return 'white';
    }
  }};
  font-size: 0.8rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: ${props => {
    switch (props.$type) {
      case 'error': return 'rgba(255, 71, 87, 0.1)';
      case 'warning': return 'rgba(255, 165, 2, 0.1)';
      case 'success': return 'rgba(46, 213, 115, 0.1)';
      default: return 'rgba(255, 255, 255, 0.05)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.$type) {
      case 'error': return 'rgba(255, 71, 87, 0.3)';
      case 'warning': return 'rgba(255, 165, 2, 0.3)';
      case 'success': return 'rgba(46, 213, 115, 0.3)';
      default: return 'rgba(255, 255, 255, 0.1)';
    }
  }};
  border-radius: 6px;
`;

// Profile Picture Editor Styles
export const ProfilePictureEditor = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1rem 0;
`;

export const ProfilePictureContainer = styled.div`
  position: relative;
  width: 150px;
  height: 150px;
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
  border: 4px solid ${theme.colors.primary};
  box-shadow: ${theme.shadows.medium};
  margin-bottom: 1rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 120px;
    height: 120px;
    border-width: 3px;
  }
`;

export const ProfilePictureImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const EditPictureButton = styled.button`
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  transition: all 0.2s ease;
  line-height: 1;

  &:hover {
    background: ${theme.colors.hover.primary};
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.medium};
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.7rem;
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
  }
`;

export const ProfilePicturePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.text.muted};
  font-size: 3rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 2.5rem;
  }
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

export const BioPreview = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.md};
  padding: 1rem;
  margin-top: 1rem;

  h4 {
    color: ${theme.colors.text.primary};
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  p {
    color: ${theme.colors.text.secondary};
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

export const BioTips = styled.div`
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.md};
  padding: 1rem;
  margin-top: 1rem;
  border: 1px solid ${theme.colors.border.light};

  h4 {
    color: ${theme.colors.text.primary};
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
`;

// Interest Selector Styles
export const InterestSelectorContainer = styled.div`
  padding: 1rem;
`;

export const InterestSelectorHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

export const InterestSelectorTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

export const InterestSelectorDescription = styled.p`
  font-size: 1rem;
  color: ${theme.colors.text.secondary};
  line-height: 1.5;
`;

export const InterestStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

export const InterestActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
`;

export const ClearButton = styled.button`
  background: #ff4757;
  border: none;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background: #ff3742;
    transform: translateY(-2px);
  }
`;

export const CustomInterestInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  background: ${theme.colors.background.card};
  border: 1px solid ${theme.colors.border.medium};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.primary};
  font-size: 0.9rem;

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px ${theme.colors.primary}20;
  }
`;

export const InterestSearch = styled.div`
  margin-bottom: 1rem;
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  background: ${theme.colors.background.card};
  border: 1px solid ${theme.colors.border.medium};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.primary};
  font-size: 0.9rem;

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px ${theme.colors.primary}20;
  }
`;

export const InterestCategories = styled.div`
  margin-bottom: 1rem;
`;

export const CategoryTab = styled.button<{ $isActive: boolean }>`
  background: ${props => props.$isActive 
    ? theme.colors.gradients.primary
    : 'transparent'};
  border: 1px solid ${props => props.$isActive 
    ? 'transparent' 
    : theme.colors.border.medium};
  color: ${props => props.$isActive ? 'white' : theme.colors.text.primary};
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.3s ease;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
  white-space: nowrap;

  &:hover {
    background: ${props => props.$isActive 
      ? theme.colors.gradients.primary
      : theme.colors.background.tertiary};
    border-color: ${props => props.$isActive ? 'transparent' : theme.colors.primary};
  }
`;

export const InterestIcon = styled.span`
  font-size: 1.2rem;
  margin-right: 0.5rem;
`;

export const InterestName = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const InterestGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

export const InterestItem = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  padding: 1rem;
  background: ${props => props.$isSelected 
    ? `${theme.colors.primary}20` 
    : theme.colors.background.secondary};
  border: 1px solid ${props => props.$isSelected 
    ? theme.colors.primary 
    : theme.colors.border.light};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.3s ease;
  height: 100%;

  &:hover {
    background: ${props => props.$isSelected 
      ? `${theme.colors.primary}30` 
      : theme.colors.background.tertiary};
    border-color: ${theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.light};
  }
`;

export const SelectedInterests = styled.div`
  margin-bottom: 2rem;
`;

export const SelectedInterest = styled.div`
  display: inline-flex;
  align-items: center;
  background: ${theme.colors.primary}20;
  border: 1px solid ${theme.colors.primary};
  border-radius: 20px;
  padding: 0.5rem 1rem;
  margin: 0.25rem;
  font-size: 0.8rem;
  color: ${theme.colors.text.primary};
`;

export const InterestCounter = styled.div`
  text-align: center;
  margin-bottom: 1rem;
`;

// About Editor Styles
export const AboutEditorContainer = styled.div`
  padding: 1rem;
`;

export const AboutEditorHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

export const AboutEditorTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

export const AboutEditorDescription = styled.p`
  font-size: 1rem;
  color: ${theme.colors.text.secondary};
  line-height: 1.5;
`;

export const AboutEditorStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

export const AboutEditorContent = styled.div`
  margin-bottom: 2rem;
`;

export const AboutEditorActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
`;

export const AboutEditorTips = styled.div`
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.md};
  padding: 1rem;
  margin-top: 1rem;
  border: 1px solid ${theme.colors.border.light};

  h4 {
    color: ${theme.colors.text.primary};
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
`;

export const DateInput = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: 0.75rem;
  background: ${theme.colors.background.card};
  border: 1px solid ${props => props.$hasError ? theme.colors.error : theme.colors.border.medium};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.primary};
  font-size: 0.9rem;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 2px ${props => props.$hasError ? `${theme.colors.error}20` : `${theme.colors.primary}20`};
  }
`;

// Location Editor Styles
export const LocationEditorContainer = styled.div`
  padding: 1rem;
`;

export const LocationEditorHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

export const LocationEditorTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

export const LocationEditorDescription = styled.p`
  font-size: 1rem;
  color: ${theme.colors.text.secondary};
  line-height: 1.5;
`;

export const LocationStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

export const LocationForm = styled.div`
  margin-bottom: 2rem;
`;

export const LocationSearch = styled.div`
  margin-bottom: 1rem;
`;

export const LocationSuggestions = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.md};
  max-height: 200px;
  overflow-y: auto;
  margin-top: 0.5rem;
`;

export const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

export const FormLabel = styled.label`
  display: block;
  color: ${theme.colors.text.primary};
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

export const FormInput = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: 0.75rem;
  background: ${theme.colors.background.card};
  border: 1px solid ${props => props.$hasError ? theme.colors.error : theme.colors.border.medium};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.primary};
  font-size: 0.9rem;

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 2px ${props => props.$hasError ? `${theme.colors.error}20` : `${theme.colors.primary}20`};
  }
`;

export const FormSelect = styled.select<{ $hasError?: boolean }>`
  width: 100%;
  padding: 0.75rem;
  background: ${theme.colors.background.card};
  border: 1px solid ${props => props.$hasError ? theme.colors.error : theme.colors.border.medium};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.primary};
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 2px ${props => props.$hasError ? `${theme.colors.error}20` : `${theme.colors.primary}20`};
  }

  option {
    background: ${theme.colors.background.card};
    color: ${theme.colors.text.primary};
  }
`;

export const LocationMap = styled.div`
  margin-bottom: 2rem;
`;

export const MapContainer = styled.div`
  width: 100%;
  height: 300px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
`;

export const MapMarker = styled.div`
  width: 20px;
  height: 20px;
  background: #667eea;
  border-radius: 50%;
  border: 2px solid white;
  position: absolute;
  transform: translate(-50%, -50%);
`;

export const PrivacySettings = styled.div`
  margin-top: 2rem;
`;

export const PrivacyOption = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

export const PrivacyCheckbox = styled.input`
  margin-right: 0.75rem;
`;

export const PrivacyLabel = styled.label`
  color: ${theme.colors.text.primary};
  font-size: 0.9rem;
  cursor: pointer;
`;

export const LocationPreview = styled.div`
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.md};
  padding: 1rem;
  margin-top: 1rem;
  border: 1px solid ${theme.colors.border.light};

  h4 {
    color: ${theme.colors.text.primary};
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  p {
    color: ${theme.colors.text.secondary};
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

export const LocationActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

export const AddCustomButton = styled.button`
  background: ${theme.colors.gradients.primary};
  border: none;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background: ${theme.colors.hover.primary};
    transform: translateY(-1px);
  }
`;

// Slide-in Drawer Styles
export const DrawerBackdrop = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 1000;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

export const DrawerContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  max-width: 600px;
  height: 100vh;
  background: ${theme.colors.background.card};
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  transform: translateX(${props => props.$isOpen ? '0' : '100%'});
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-left: 1px solid ${theme.colors.border.light};

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

export const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid ${theme.colors.border.light};
  background: ${theme.colors.background.secondary};
`;

export const DrawerTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

export const DrawerCloseButton = styled.button`
  background: ${theme.colors.background.tertiary};
  border: 1px solid ${theme.colors.border.medium};
  color: ${theme.colors.text.primary};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.primary};
    color: white;
    border-color: ${theme.colors.primary};
    transform: rotate(90deg);
  }
`;

export const DrawerContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  background: ${theme.colors.background.card};
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${theme.colors.background.secondary};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.border.medium};
    border-radius: 4px;
    
    &:hover {
      background: ${theme.colors.border.dark};
    }
  }
`;
